import { useMemo, type ReactNode } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { canViewField } from '@wlb/shared/utils/profileVisibility';
import type { ViewerContext } from '@wlb/shared/utils/profileVisibility';
import type { Profile } from '@wlb/shared/types';
import { Avatar } from '../../components/ui/Avatar';
import { getProfileAvatarUrl } from './useCurrentProfile';
import { buildProfileChips } from './profileChips';
import { ProfileInfoChips } from './ProfileInfoChips';
import { ProfileStatsBar, type ProfileConnectionStatKey } from './ProfileStatsBar';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import './profile.css';

interface ProfileInfoCardProps {
  profile: Profile;
  viewer: ViewerContext;
  onViewPhoto?: () => void;
  onSelectStat?: (key: ProfileConnectionStatKey) => void;
  /** The two call-to-action buttons under the chips. */
  actions?: ReactNode;
}

export function ProfileInfoCard({
  profile,
  viewer,
  onViewPhoto,
  onSelectStat,
  actions,
}: ProfileInfoCardProps) {
  const { t } = useTranslation();
  const chips = useMemo(() => buildProfileChips(profile, viewer), [profile, viewer]);
  const showBio = Boolean(profile.bio) && canViewField(profile.bioPrivacy, viewer);
  const avatarUrl = canViewField(profile.profilePicturePrivacy, viewer)
    ? getProfileAvatarUrl(profile)
    : '';
  const hasPhotoAction = Boolean(onViewPhoto) && (Boolean(avatarUrl) || Boolean(viewer.isSelf));
  const username = getUsernameDisplay(profile);

  return (
    <section className="profile-card">
      <div className="profile-card__identity">
        <div className="profile-card__avatar">
          {hasPhotoAction ? (
            <button
              type="button"
              className="profile-card__avatar-button"
              aria-label={t(
                avatarUrl
                  ? 'profile.photoViewer.viewPhoto'
                  : 'profile.accessibility.changePhoto',
              )}
              onClick={onViewPhoto}
            >
              <Avatar
                src={avatarUrl}
                name={username}
                username={username}
                seed={profile.userGuid ?? profile.userId}
                profileUserGuid={profile.userGuid}
                userId={profile.userId}
                canonical
                size="xl"
                square
              />
            </button>
          ) : (
            <Avatar
              src={avatarUrl}
              name={username}
              username={username}
              seed={profile.userGuid ?? profile.userId}
              profileUserGuid={profile.userGuid}
              userId={profile.userId}
              canonical
              size="xl"
              square
            />
          )}
        </div>
        <div className="profile-card__identity-text">
          <h1 className="profile-card__name">{username}</h1>
          {showBio && <p className="profile-card__bio">{profile.bio}</p>}
        </div>
      </div>

      <ProfileStatsBar profile={profile} onSelect={onSelectStat} />
      <ProfileInfoChips chips={chips} />

      {actions && <div className="profile-card__actions">{actions}</div>}
    </section>
  );
}
